import { Card, CardContent } from "../../ui/card"
import { SummaryHeader } from "../../ui/SummaryHeader"
import { useChartColors } from "../../../hooks/useChartColors"
import SalesLoader from "../../../components/loaders/SalesLoader"
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useMemo } from "react"
import { useWholesaleSales } from "../hooks/useSalesWholesale";
import { DataTable } from "../../ui/data-table/data-table"
import { formatNumber } from "../../../utils/formatNumber";
import { formatCurrency } from "../../../utils/formatCurrency";


interface ClassPerformanceProps {
  selectedMonth: string;
}


interface ClassPerformanceMetrics {
  class: string;
  class_description: string;
  units_sold: number;
  revenue: number;
  average_order: number;
}

export function WholesaleClassPerformance({ selectedMonth }: ClassPerformanceProps) {

  const monthDate = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }, [selectedMonth]);

  const startDate = format(startOfMonth(monthDate), "yyyy-MM-dd");
  const endDate = format(endOfMonth(monthDate), "yyyy-MM-dd");


  const { data, isLoading } = useWholesaleSales(startDate, endDate);

  const wholesaleData: ClassPerformanceMetrics[] = useMemo(() =>
    (data?.wholesale_class_performance || []).map((cat) => ({
      class: cat.class,
      class_description: cat.class_description,
      units_sold: cat.units_sold,
      revenue: cat.revenue,
      average_order: cat.average_order,
    }))
    , [data]);

  const noData = wholesaleData.length === 0;


  const columns = [
  {
    accessorKey: "class",
    header: "Class",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "class_description",
    header: "Description",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "units_sold",
    header: "Units Sold",
    isNumber: true,
    cell: (info: any) => formatNumber(info.getValue()),
  },
  {
    accessorKey: "average_order",
    header: "AOV",
    isNumber: true,
    cell: (info: any) => formatCurrency(info.getValue()),
  },
    {
    accessorKey: "revenue",
    header: "Revenue",
    isNumber: true,
    cell: (info: any) => formatCurrency(info.getValue()),
  },
];

  const colors = useChartColors("wholesale")
  if (!colors) return null;
  return (
    <>
      <SummaryHeader
        title="Class"
      />
      <Card>
        <CardContent>
                {isLoading ? (
                  <SalesLoader variant="performance-channel" />
                ) : noData ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No Data Available
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={wholesaleData}
                    searchColumn="class"
                    searchPlaceholder="Search by Class..."
                    showRowsPerPage={false}
                    initialPageSize={5}
                    showTotal={true} // Enable totals row
                    totalColumns={["revenue"]} // Columns to total
                  />
                )}             
        </CardContent>
      </Card>
    </>
  )
}
